<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'JobRunView',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    item: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const onClick = (...args) => {
    const [ev] = args;
    const event = ev.originalEvent;

    if (event.detail === 3) {
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(event.target);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const result_text = computed(() => {
    return (
      (props.item.result_data && props.item.result_data.message) ||
      props.item.result_text
    );
  });
  const details = computed(() => {
    return (
      (props.item.result_data && props.item.result_data.details) ||
      props.item.log
    );
  });

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );

  const act_6917646200545441_script_e4t0x6vn5_value_callable = async (
    $event
  ) => {
    return onClick($event);
  };
  const act_6917646200545441_script_e4t0x6vn5_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_6917646200545441_script_e4t0x6vn5_value_callable($event);
      },
    };
  };

  const act_6917646200545441_script_e4t0x6vn5 = new actionTypes.ScriptAction({
    actionArgs: act_6917646200545441_script_e4t0x6vn5_getActionArgs,
  });
  const inst_evh_6917646200545441_clickStop_sbxf5vwu8 =
    new actionTypes.EventHandlerImpl({
      action: act_6917646200545441_script_e4t0x6vn5,
      event: 'click.stop',
      displayName: 'script',
    });
  const inst_evh_6917646200545441_clickStop = new actionTypes.EventHandlerGroup(
    {
      handlers: [inst_evh_6917646200545441_clickStop_sbxf5vwu8],
      event: 'click.stop',
    }
  );

  const actions = {
    inst_evh_6917646200545441_clickStop_sbxf5vwu8,
    inst_evh_6917646200545441_clickStop,
  };

  const evh_6917646200545441_clickStop_sbxf5vwu8 = async (event, scope) => {
    return await actions.inst_evh_6917646200545441_clickStop_sbxf5vwu8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6917646200545441_clickStop = async (event, scope) => {
    return await actions.inst_evh_6917646200545441_clickStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox direction="column" class="overflow-hidden h-100">
    <JobProgress :item="item"> </JobProgress>
    <ItpBox direction="row">
      <ItpText
        muted
        :text="pgettext('Job', 'Start time')"
        class="me-2"
        v-if="item.start_time"
      >
      </ItpText>
      <ItpText strong class="me-4" v-if="item.start_time">
        {{ datetime(item.start_time) }}
      </ItpText>
      <ItpText
        muted
        :text="pgettext('Job', 'End time')"
        class="me-2"
        v-if="item.end_time"
      >
      </ItpText>
      <ItpText strong class="me-2" v-if="item.end_time">
        {{ datetime(item.end_time) }}
      </ItpText>
    </ItpBox>
    <ItpAlert show v-if="result_text && result_text != details">
      <span>
        {{ result_text }}
      </span>
    </ItpAlert>
    <div class="border mt-2 overflow-auto" v-if="details">
      <pre
        class="m-0 p-3 h-100 w-100"
        @click.stop="evh_6917646200545441_clickStop($event, {})"
        >{{ details }}</pre
      >
    </div>
    <JobFiles :item="item" v-if="item.files && item.files.length"> </JobFiles>
  </ItpBox>
</template>
